import { useState, useRef } from 'react';
import { postRequest } from '../../../../config';
import { URL as SERVER_URL } from '../../../../env';

const ACCEPTED_EXTS = ['.xlsx', '.xls', '.csv'];

const OBJECTIVE_LABELS = { Investment: 'Inv', Growth: 'Gro', Exit: 'Exi' };
const TIER_COLORS = { Free: '#4A67FF', Lite: '#038589', Pro: '#FC7785' };

/**
 * ExcelUpload
 *
 * Props:
 *   moduleKey        {string}    — current module key
 *   parsedData       {object}    — result from /modules/parse-excel
 *   importedVariants {string[]}  — variant keys that have been applied to state
 *   onParsed         {fn}        — called with parsed body after successful upload
 *   onApply          {fn}        — opens the variant selection modal (first import)
 *   onReselect       {fn}        — opens modal again to change selection
 */
const ExcelUpload = ({ moduleKey, onParsed, onApply, onReselect, parsedData, importedVariants = [] }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');
    const [fileName, setFileName] = useState('');
    const inputRef = useRef(null);

    const uploadFile = async (file) => {
        const ext = file.name.split('.').pop().toLowerCase();
        if (!ACCEPTED_EXTS.includes('.' + ext)) {
            setError(`Unsupported file type ".${ext}". Please upload an xlsx, xls, or csv file.`);
            return;
        }

        setIsUploading(true);
        setError('');
        setFileName(file.name);

        try {
            // Read as base64 to avoid conflicts with the global multer middleware
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    // result is "data:<mime>;base64,<data>" — strip the prefix
                    const raw = e.target.result;
                    resolve(raw.split(',')[1] || raw);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

            const result = await postRequest('/modules/parse-excel', { fileContent: base64, fileName: file.name }, true, undefined, true);

            if (result && result['code'] === 200) {
                onParsed(result['body']);
            } else {
                setError(result?.['message'] || 'Failed to parse file. Please check the format and try again.');
            }
        } catch (e) {
            setError('Upload failed. Please check your connection and try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) uploadFile(file);
    };

    const handleFileInput = (e) => {
        const file = e.target?.files?.[0];
        if (file) uploadFile(file);
        e.target.value = '';
    };

    const colLabel = {
        section:            'Section',
        subsection:         'Sub-section',
        question:           'Question',
        questionType:       'Question Type',
        tooltip:            'Tool Tip',
        toolbox:            'Tool Box',
        risk:               'Risk Description',
        recommendation:     'Recommendation',
        riskScore:          'Risk Score',
        attachmentRequired: 'Attachment Required',
        options:            'Options',
        ifYes:              'Info if Yes',
        ifNo:               'Info if No',
        riskIfYes:          'Risk if Yes',
        riskIfNo:           'Risk if No',
        reference:          'Reference',
    };

    const handleDownloadTemplate = async () => {
        try {
            const authToken = localStorage.getItem('token') || '';
            const authUserId = localStorage.getItem('userId') || '';
            const res = await fetch(`${SERVER_URL}/api/modules/sample-template`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'id': authUserId,
                },
            });
            if (!res.ok) { setError('Failed to download template. Please try again.'); return; }
            const blob = await res.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = 'module_import_template.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch (e) {
            setError('Failed to download template. Please try again.');
        }
    };

    return (
        <div className="df fdc" style={{ gap: '1rem' }}>
            {/* Download sample button */}
            <div className="df jcfe">
                <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="df aic br50px px1-25rem py0-5rem fw600 fs0-8rem td300 cp"
                    style={{ background: '#fff', border: '1px solid #4A67FF', color: '#4A67FF', gap: '0.4rem' }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download Sample
                </button>
            </div>
            {/* Drop zone */}
            <div
                onClick={() => !isUploading && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className="df fdc aic jcc br10px bss bw2px cp td300"
                style={{
                    borderColor: isDragging ? '#4A67FF' : '#DDE2F2',
                    background:  isDragging ? '#f0f2fd' : '#F5F6F9',
                    minHeight:   '160px',
                    gap:         '0.75rem',
                    padding:     '2rem'
                }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="pa"
                    style={{ opacity: 0, width: 0, height: 0 }}
                    onChange={handleFileInput}
                />

                {isUploading ? (
                    <>
                        <div style={{ width: 36, height: 36, border: '3px solid #4A67FF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        <p className="fs0-9rem fw500 c15 m0" style={{ opacity: 0.7 }}>Parsing {fileName}…</p>
                    </>
                ) : parsedData ? (
                    <>
                        <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: '#40dda1' }} />
                        <p className="fs0-9rem fw600 c15 m0">{fileName || 'File parsed'}</p>
                        <p className="fs0-8rem c15 m0" style={{ opacity: 0.6 }}>Click to replace with a different file</p>
                    </>
                ) : (
                    <>
                        <i className="fas fa-file-excel" style={{ fontSize: '2.5rem', color: '#40dda1' }} />
                        <div className="tac">
                            <p className="fs0-95rem fw600 c15 m0">Drop your Excel file here</p>
                            <p className="fs0-8rem c15 m0 mt0-25rem" style={{ opacity: 0.6 }}>or click to browse — xlsx, xls, csv</p>
                        </div>
                    </>
                )}
            </div>

            {/* Error */}
            {error && (
                <div className="df aic br8px p0-75rem" style={{ background: '#fff0f0', border: '1px solid #FC7785', gap: '0.5rem' }}>
                    <i className="fas fa-exclamation-circle c4" />
                    <p className="fs0-85rem c15 m0">{error}</p>
                </div>
            )}

            {/* Parsed preview */}
            {parsedData && !isUploading && (
                <div className="df fdc br10px bss bw1px" style={{ borderColor: '#DDE2F2', overflow: 'hidden' }}>
                    {/* Summary bar */}
                    <div className="df jcsb aic px1-5rem py1rem" style={{ background: '#f0f2fd', borderBottom: '1px solid #DDE2F2', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <div className="df aic" style={{ gap: '1.5rem' }}>
                            <div className="df fdc">
                                <span className="fs1-25rem fw600 c3">{parsedData['totalSections']}</span>
                                <span className="fs11px fw500 c15" style={{ opacity: 0.6 }}>Sections</span>
                            </div>
                            <div className="df fdc">
                                <span className="fs1-25rem fw600 c3">{parsedData['totalQuestions']}</span>
                                <span className="fs11px fw500 c15" style={{ opacity: 0.6 }}>Questions</span>
                            </div>
                            {parsedData['errors']?.length > 0 && (
                                <div className="df fdc">
                                    <span className="fs1-25rem fw600 c4">{parsedData['errors'].length}</span>
                                    <span className="fs11px fw500 c15" style={{ opacity: 0.6 }}>Skipped rows</span>
                                </div>
                            )}
                        </div>

                        {importedVariants.length === 0 ? (
                            <button
                                type="button"
                                onClick={onApply}
                                className="br50px px1-5rem py0-5rem fw600 fs0-85rem c1 td250 cp bss bw0px"
                                style={{ background: '#4A67FF' }}
                            >
                                Select Combinations →
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={onReselect}
                                className="br50px px1-5rem py0-5rem fw600 fs0-85rem td250 cp"
                                style={{ background: '#fff', border: '1px solid #4A67FF', color: '#4A67FF' }}
                            >
                                Change Selection
                            </button>
                        )}
                    </div>

                    {/* Import summary — shown after user confirmed combinations */}
                    {importedVariants.length > 0 && (
                        <div className="px1-5rem py1rem df fdc" style={{ gap: '0.75rem', borderBottom: '1px solid #DDE2F2', background: '#e8fdf3' }}>
                            <div className="df aic" style={{ gap: '0.5rem' }}>
                                <i className="fas fa-check-circle" style={{ color: '#09B255', fontSize: '1rem' }} />
                                <p className="fs0-875rem fw600 m0" style={{ color: '#09B255' }}>
                                    Ready to save — {parsedData['totalSections']} sections · {parsedData['totalQuestions']} questions loaded into {importedVariants.length} combination{importedVariants.length > 1 ? 's' : ''}
                                </p>
                            </div>
                            <div className="df fww" style={{ gap: '0.4rem' }}>
                                {importedVariants.map((vk) => {
                                    const [obj, tier] = vk.split('-');
                                    return (
                                        <span
                                            key={vk}
                                            className="br20px px0-75rem py0-25rem fs11px fw600"
                                            style={{ background: TIER_COLORS[tier] + '20', border: `1px solid ${TIER_COLORS[tier]}`, color: TIER_COLORS[tier] }}
                                        >
                                            {obj} · {tier}
                                        </span>
                                    );
                                })}
                            </div>
                            <p className="fs0-8rem c15 m0" style={{ opacity: 0.6 }}>
                                Click <strong>Save Module</strong> below to persist these sections to the database.
                            </p>
                        </div>
                    )}

                    {/* Column mapping */}
                    <div className="px1-5rem py1rem">
                        <p className="fs0-8rem fw600 c15 mb0-5rem" style={{ opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Detected columns</p>
                        <div className="df fww" style={{ gap: '0.4rem' }}>
                            {Object.entries(parsedData['columnMapping'] || {}).map(([key, val]) => (
                                <span
                                    key={key}
                                    className="br20px px0-75rem py0-25rem fs11px fw500"
                                    style={{
                                        background: val ? '#e8f5e9' : '#fafafa',
                                        border:     `1px solid ${val ? '#a5d6a7' : '#e0e0e0'}`,
                                        color:      val ? '#2e7d32' : '#bdbdbd'
                                    }}
                                >
                                    {colLabel[key] || key}: {val ? `"${val}"` : '—'}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Section breakdown */}
                    <div className="px1-5rem pb1rem" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                        <p className="fs0-8rem fw600 c15 mb0-5rem" style={{ opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sections</p>
                        <div className="df fdc" style={{ gap: '0.35rem' }}>
                            {(parsedData['sections'] || []).map((sec, i) => (
                                <div key={i} className="df jcsb aic" style={{ padding: '0.3rem 0', borderBottom: '1px solid #f0f0f0' }}>
                                    <span className="fs0-85rem c15" style={{ opacity: 0.85 }}>{sec['sectionName']}</span>
                                    <span className="fs11px fw600 br20px px0-5rem py0-15rem" style={{ background: '#e8eaf6', color: '#3949ab' }}>
                                        {sec['questions']?.length || 0}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Skipped row errors */}
                    {parsedData['errors']?.length > 0 && (
                        <div className="px1-5rem pb1rem">
                            <p className="fs0-8rem fw600 c4 mb0-5rem">Skipped rows</p>
                            <div className="df fdc" style={{ gap: '0.25rem', maxHeight: '80px', overflowY: 'auto' }}>
                                {parsedData['errors'].map((err, i) => (
                                    <p key={i} className="fs11px c15 m0" style={{ opacity: 0.65 }}>
                                        Line {err['line']}: {err['message']}
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExcelUpload;
